import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Search, X } from 'lucide-react';
import { Popover } from '@/components/ui';
import { cn } from '@/lib/utils';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { useRecordSearch } from '@/store/searchHistoryStore';
import {
  useSearchOverlayOpen,
  useOpenSearchOverlay,
  useCloseSearchOverlay,
  useSearchQuery,
  useSetSearchQuery,
} from '@/store';
import { SearchResultsFeed } from '@/components/features/Search/SearchResultsFeed';
import { SearchEntryState } from '@/components/features/Search/SearchEntryState';

const TEST_ID_PREFIX = 'desktop-search';

export interface DesktopHeaderSearchProps {
  /**
   * Compact mode mirrors the pinned header: shrinks the field so it lines up
   * with the compact category tabs. Forwarded from {@link CategoryTabs}.
   */
  compact?: boolean;
}

/**
 * Desktop (md+) search: a default-open pill field sitting inline before the
 * category tabs (Figma 6492-182800 "Categorys" — desktop lobby). Unlike the
 * mobile {@link HeaderSearch}, there is no collapsed icon state and the page
 * never gets taken over: focusing or typing opens an anchored {@link Popover}
 * panel under the field with the entry state (recent + popular) or, once a
 * debounced query exists, live results ({@link SearchResultsFeed}).
 *
 * Shares the search-overlay store with the mobile UI ("open" = panel open), so
 * state survives a resize across the breakpoint. Escape and outside presses
 * close via the Popover; closing clears the query (store semantics). The
 * category filter pills are intentionally absent — on desktop the category
 * tabs sit right next to the field and results already follow the active tab.
 */
export function DesktopHeaderSearch({ compact = false }: DesktopHeaderSearchProps) {
  const isOpen = useSearchOverlayOpen();
  const open = useOpenSearchOverlay();
  const close = useCloseSearchOverlay();
  const query = useSearchQuery();
  const setQuery = useSetSearchQuery();
  const recordSearch = useRecordSearch();
  const { pathname } = useLocation();

  const debouncedQuery = useDebouncedValue(query, 250);
  const hasQuery = debouncedQuery.trim().length > 0;

  // Opening a result/popular/related row navigates to its event — close the
  // panel on any route change so it never lingers over a different page.
  useEffect(() => {
    if (isOpen) close();
    // Run only when the path changes — `close`/`isOpen` deliberately omitted.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  function pickRecent(recent: string) {
    setQuery(recent);
    recordSearch(recent);
  }

  const anchor = (
    <div className="relative flex items-center">
      <Search
        aria-hidden
        className={cn(
          'pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-purple',
          compact ? 'size-4' : 'size-[18px]',
        )}
      />
      <input
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          if (!isOpen) open();
        }}
        onFocus={open}
        onKeyDown={(e) => {
          if (e.key === 'Enter') recordSearch(query);
        }}
        placeholder="Search here"
        aria-label="Search events"
        data-testid={`${TEST_ID_PREFIX}-input`}
        className={cn(
          'w-[249px] rounded-full border bg-surface font-sans text-ink placeholder:text-muted',
          'pl-10 pr-9 transition-colors focus:outline-none',
          isOpen ? 'border-purple' : 'border-border',
          compact ? 'h-9 text-sm' : 'h-10 text-base',
        )}
      />
      {query.length > 0 && (
        <button
          type="button"
          onClick={() => setQuery('')}
          aria-label="Clear search"
          data-testid={`${TEST_ID_PREFIX}-clear`}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted hover:text-primary"
        >
          <X className="size-4" />
        </button>
      )}
    </div>
  );

  return (
    <div className="shrink-0" data-testid={TEST_ID_PREFIX}>
      <Popover open={isOpen} onClose={close} anchor={anchor} align="start" className="w-[480px]">
        <div className="max-h-[min(560px,70vh)] overflow-y-auto px-4 pt-4">
          {hasQuery ? (
            <SearchResultsFeed query={debouncedQuery} onResultOpen={recordSearch} />
          ) : (
            <SearchEntryState onPickRecent={pickRecent} />
          )}
        </div>
      </Popover>
    </div>
  );
}
